import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../features/auth/AuthContext";
import { apiGet } from "../lib/api";
import { formatPrice } from "../lib/formatters";
import { mediaUrl } from "../lib/media";
import { statusLabel, useT } from "../i18n";
import { OrderStatusBadge, formatOrderDate } from "./OrdersPage";

// Order lifecycle as stored on the order (orders.status).
const STEPS = [
  { key: "pending", label: "Order received", text: "The seller has your order and will confirm it." },
  { key: "confirmed", label: "Confirmed", text: "The seller confirmed availability and fitment." },
  { key: "shipped", label: "Shipped", text: "Your parts are on the way." },
  { key: "delivered", label: "Delivered", text: "The order was delivered." },
];
const STEP_INDEX = { pending: 0, confirmed: 1, processing: 1, shipped: 2, delivered: 3 };

// Real order details and status for the signed-in buyer
// (GET /api/v1/commerce/orders/:reference).
export default function OrderTrackingPage() {
  const t = useT();
  const { orderNumber } = useParams();
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState({ loading: true, error: "", order: null });

  useEffect(() => {
    if (!isAuthenticated || !orderNumber) return undefined;
    let active = true;
    apiGet(`/api/v1/commerce/orders/${encodeURIComponent(orderNumber)}`, undefined, { auth: true })
      .then((res) => active && setState({ loading: false, error: "", order: res?.data || null }))
      .catch((err) => active && setState({ loading: false, error: err?.message || t("Could not load this order"), order: null }));
    return () => {
      active = false;
    };
  }, [isAuthenticated, orderNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) {
    return (
      <section className="mx-auto max-w-xl rounded-3xl border border-line bg-surface p-8 text-center shadow-sm">
        <h1 className="text-3xl font-bold text-ink">{t("Sign in to see this order")}</h1>
        <p className="mt-2 text-sm text-muted">{t("Orders are linked to the account that placed them.")}</p>
        <Link
          to={`/auth?redirect=${encodeURIComponent(`/orders/${orderNumber || ""}/tracking`)}`}
          className="btn-cta mt-6 h-11 rounded-xl px-6 text-sm"
        >
          {t("Sign in")}
        </Link>
      </section>
    );
  }

  const { loading, error, order } = state;
  const back = (
    <Link to="/orders" className="text-sm font-semibold text-link hover:underline">
      <span aria-hidden="true" className="inline-block rtl:rotate-180">←</span> {t("My Orders")}
    </Link>
  );

  if (loading) {
    return (
      <section className="space-y-4" aria-busy="true">
        {back}
        <div className="h-40 animate-pulse rounded-3xl border border-line bg-surface" />
        <div className="h-64 animate-pulse rounded-3xl border border-line bg-surface" />
      </section>
    );
  }

  if (error || !order) {
    return (
      <section className="space-y-4">
        {back}
        <div className="rounded-3xl border border-line bg-surface p-8 text-center">
          <p className="text-lg font-semibold text-ink">{t("Order not found")}</p>
          <p className="mt-1 text-sm text-muted">{error || t("Check the order number in your account.")}</p>
        </div>
      </section>
    );
  }

  const cancelled = order.status === "cancelled";
  const current = STEP_INDEX[order.status] ?? 0;
  const items = Array.isArray(order.items) ? order.items : [];
  const address = order.shipping_address;

  return (
    <section className="space-y-6 pb-10">
      {back}

      <div className="rounded-3xl border border-line bg-surface p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted">{t("Order")}</p>
            <h1 className="mt-1 font-mono text-2xl font-bold text-ink">{order.reference}</h1>
            <p className="mt-1 text-sm text-muted">{t("Placed on {date}", { date: formatOrderDate(order.created_at) })}</p>
          </div>
          <OrderStatusBadge status={order.status} />
        </div>

        {cancelled ? (
          <p className="mt-5 rounded-2xl border border-red-400/30 bg-red-400/10 p-4 text-sm text-red-800">
            {t("This order was cancelled.")}
          </p>
        ) : (
          <ol className="mt-6 grid gap-3 sm:grid-cols-4">
            {STEPS.map((step, i) => {
              const done = i <= current;
              return (
                <li
                  key={step.key}
                  aria-current={i === current ? "step" : undefined}
                  className={[
                    "rounded-2xl border p-4",
                    done ? "border-emerald-400/40 bg-emerald-400/10" : "border-line bg-surface-2",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold",
                        done ? "bg-emerald-600 text-white" : "bg-line-strong text-white",
                      ].join(" ")}
                      aria-hidden="true"
                    >
                      {done ? "✓" : i + 1}
                    </span>
                    <p className={["text-sm font-semibold", done ? "text-emerald-900" : "text-muted"].join(" ")}>{t(step.label)}</p>
                  </div>
                  <p className="mt-2 text-xs text-muted">{t(step.text)}</p>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="rounded-3xl border border-line bg-surface p-5 shadow-sm">
          <h2 className="text-lg font-bold text-ink">{t("Items")}</h2>
          <ul className="mt-3 divide-y divide-line">
            {items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 py-3">
                <div className="media-well flex h-14 w-14 flex-none items-center justify-center overflow-hidden rounded-lg">
                  {item.thumbnail ? (
                    <img src={mediaUrl(item.thumbnail)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span aria-hidden="true">⚙️</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-sm font-semibold text-ink">{item.title}</p>
                  <p className="text-xs text-muted">
                    {t("{price} each", { price: formatPrice(item.price) })} × {item.quantity}
                  </p>
                </div>
                <p className="font-mono text-sm font-semibold text-ink">{formatPrice(item.subtotal)}</p>
              </li>
            ))}
          </ul>
          <dl className="mt-3 grid grid-cols-[1fr_auto] gap-y-1.5 border-t border-line pt-3 text-sm">
            <dt className="text-muted">{t("Subtotal")}</dt>
            <dd className="text-end font-mono">{formatPrice(order.subtotal)}</dd>
            <dt className="text-muted">{t("Shipping")}</dt>
            <dd className="text-end font-mono">{formatPrice(order.shipping_fee)}</dd>
            <dt className="font-semibold text-ink">{t("Total")}</dt>
            <dd className="text-end font-mono font-semibold text-ink">{formatPrice(order.total)}</dd>
          </dl>
        </div>

        <aside className="space-y-4">
          {address ? (
            <div className="rounded-3xl border border-line bg-surface p-5 shadow-sm">
              <h2 className="text-lg font-bold text-ink">{t("Delivery details")}</h2>
              <div className="mt-2 space-y-0.5 text-sm text-ink-2">
                <p className="font-semibold">{address.full_name}</p>
                <p>{address.address_line1}</p>
                {address.address_line2 ? <p>{address.address_line2}</p> : null}
                <p>{[address.postal_code, address.city].filter(Boolean).join(" ")}</p>
                <p dir="ltr" className="text-start">{address.phone}</p>
              </div>
            </div>
          ) : null}
          <div className="rounded-3xl border border-line bg-surface p-5 text-sm text-muted shadow-sm">
            <p className="font-semibold text-ink">{t("Payment")}</p>
            <p className="mt-1">{t("Payment status: {status}", { status: statusLabel(order.payment_status || "unpaid") })}</p>
            <p className="mt-3">{t("The seller may call you to confirm the part before shipping.")}</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
